import { BUILD_ID, APP_VERSION } from './constants';
import { getVersionInfo, checkNeedsUpdate, hasReloadedThisSession, markReloadedThisSession } from './versionManager';
import { checkRemoteVersion, shouldForceUpdate } from './remoteVersionChecker';
import { performReload } from './reloadHandler';

// Much shorter cooldown - 10 seconds in dev, 5 minutes in production
const RELOAD_COOLDOWN = import.meta.env.DEV ? 10000 : 300000;

// Export constants for other modules
export { BUILD_ID, APP_VERSION };

export const initVersionControl = async (): Promise<void> => {
  try {
    // Skip version control check during testing or if we've already reloaded this session
    if (window.location.search.includes('skipVersionCheck=true') || 
        hasReloadedThisSession()) {
      console.log('Skipping version check - already reloaded this session or test mode');
      return;
    }

    const currentInfo = getVersionInfo();
    const storedInfo = localStorage.getItem('version_info');
    const currentTime = Date.now();
    
    // Always check remote version
    const remoteInfo = await checkRemoteVersion();
    
    // Force refresh if remote explicitly requests it
    if (remoteInfo?.forceRefresh === true) {
      console.log('Force refresh requested from server');
      markReloadedThisSession(); // Mark to prevent loops
      performReload(currentInfo);
      return;
    }
    
    // Check if versions differ from remote
    if (remoteInfo && remoteInfo.buildId && remoteInfo.buildId !== currentInfo.buildId) {
      console.log('Version changed: remote is different from local');
      markReloadedThisSession(); // Mark to prevent loops
      performReload(currentInfo);
      return;
    }
    
    // First visit - store version info without reload
    if (!storedInfo) {
      localStorage.setItem('version_info', JSON.stringify(currentInfo));
      return;
    }

    // Check if local versions differ and enough time has passed
    const parsedInfo = JSON.parse(storedInfo);
    const timeSinceReload = currentTime - parsedInfo.lastReload;
    
    if (checkNeedsUpdate() && timeSinceReload > RELOAD_COOLDOWN) {
      console.log('Version changed and cooldown passed, reloading');
      markReloadedThisSession(); // Mark to prevent loops
      performReload(currentInfo);
    } else {
      // Update stored version without reloading
      localStorage.setItem('version_info', JSON.stringify(currentInfo));
    }
  } catch (error) {
    console.error('Version control error:', error);
  }
};

// Re-export needed functions
export { checkNeedsUpdate, forceUpdate } from './versionManager';
